import { alpha, createTheme } from "@mui/material/styles";

/**
 * App-wide design tokens. Pages should reach for these (theme typography
 * variants, palette keys, spacing) instead of hard-coding font sizes and
 * colours, so the whole app stays consistent across monitor sizes.
 *
 * Type scale (at the default 16px root; the root grows to 17px on screens
 * 2200px and wider, so every rem-based size scales with it):
 *   h4        24px / 600  page title (PageHeader uses it)
 *   h5        20px / 600  large section title
 *   h6        17px / 600  section / card title
 *   subtitle1 15px / 600  emphasised line, group heading
 *   subtitle2 14px / 600  card label, table group label
 *   body1     15px        default reading text
 *   body2     14px        dense text: tables, forms, descriptions
 *   caption   12px        metadata, helper text - the smallest size to use
 *   overline  12px / 600  uppercase KPI labels
 * Nothing in the app should go below 12px.
 */

const FONT_STACK = [
  "Inter",
  "system-ui",
  "-apple-system",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(",");

const BRAND_BLUE = "#1565c0";
const PAGE_BG = "#f4f6f9";
const DIVIDER = "#e2e7ee";
const TABLE_HEAD_BG = "#f7f9fc";
const TEXT_PRIMARY = "#18212d";
const TEXT_SECONDARY = "#566173";

const theme = createTheme({
  palette: {
    primary: {
      main: BRAND_BLUE,
    },
    secondary: {
      main: "#dc004e",
    },
    background: {
      default: PAGE_BG,
      paper: "#ffffff",
    },
    divider: DIVIDER,
    text: {
      primary: TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: FONT_STACK,
    fontSize: 14,
    h1: { fontSize: "2.25rem", fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.01em" },
    h2: { fontSize: "1.875rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.01em" },
    h3: { fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.005em" },
    h4: { fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.005em" },
    h5: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: "0.9375rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    caption: { fontSize: "0.75rem", lineHeight: 1.45 },
    overline: { fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.6, letterSpacing: "0.06em" },
    button: { fontSize: "0.875rem", fontWeight: 500, textTransform: "none", letterSpacing: 0 },
  },
});

const focusRing = `2px solid ${alpha(BRAND_BLUE, 0.55)}`;

theme.components = {
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        // Large, high-density monitors (2560px and up) render 16px text physically
        // small; nudge the root so all rem-based type grows a step there.
        "@media (min-width: 2200px)": { fontSize: "106.25%" },
      },
      body: {
        backgroundColor: PAGE_BG,
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
      },
      ".tabular-nums": { fontVariantNumeric: "tabular-nums" },
      "a:focus-visible": { outline: focusRing, outlineOffset: 2 },
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        "&.Mui-focusVisible": { outline: focusRing, outlineOffset: 2 },
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { textTransform: "none", fontWeight: 500, borderRadius: 8 },
      sizeSmall: { fontSize: "0.8125rem" },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: 8 },
    },
  },
  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { borderBottom: `1px solid ${alpha("#000", 0.12)}` },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ ownerState }) =>
        ownerState.variant === "elevation" &&
        typeof ownerState.elevation === "number" &&
        ownerState.elevation >= 1 &&
        ownerState.elevation <= 3
          ? {
              // Flat surfaces with a hairline border read crisper on a tinted page
              // than stacked drop shadows. Menus/dialogs keep their elevation.
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
              border: `1px solid ${DIVIDER}`,
            }
          : {},
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: 8 },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 44 },
      indicator: { height: 3, borderTopLeftRadius: 3, borderTopRightRadius: 3 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 500,
        fontSize: "0.875rem",
        letterSpacing: 0,
        minHeight: 44,
        "&.Mui-selected": { fontWeight: 600 },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: "10px 14px",
        borderBottom: `1px solid ${DIVIDER}`,
        fontVariantNumeric: "tabular-nums",
      },
      sizeSmall: { padding: "6px 12px" },
      head: {
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: TEXT_SECONDARY,
        backgroundColor: TABLE_HEAD_BG,
        whiteSpace: "nowrap",
        lineHeight: 1.4,
      },
      body: { fontSize: "0.875rem" },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&.MuiTableRow-hover:hover": { backgroundColor: alpha(BRAND_BLUE, 0.04) },
        "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: alpha(BRAND_BLUE, 0.08) },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: "0.8125rem",
        lineHeight: 1.45,
        maxWidth: 380,
        padding: "6px 10px",
        backgroundColor: "#1f2937",
        borderRadius: 6,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { fontWeight: 500, fontSize: "0.8125rem" },
      sizeSmall: { fontSize: "0.75rem", height: 24 },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { backgroundColor: "#ffffff" },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 8, fontSize: "0.875rem" },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: { fontSize: "1.125rem", fontWeight: 600 },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: { fontSize: "0.875rem", minHeight: 36 },
    },
  },
};

export default theme;
